import { Auth } from "aws-amplify";
import { Component, FormEvent, FunctionComponent } from "react";
import { Form, Alert, Card, Col, Row } from "react-bootstrap";
import { RouteComponentProps } from "react-router-dom";
import { IPaymentsOptOut, IRegisterAccount } from "../../../client/core";
import { Color, Path, Storage } from "../../../env";
import { Button, icon, variant } from "../../form/Button";

export interface RegisterAccountState {
    error: string;
    state: State;
    isLoading: boolean;
}

export enum State {
    None,
    Online,
    Offline
}

export interface RegisterAccountProps extends RouteComponentProps {
    registerAccountAPI: IRegisterAccount;
    paymentOptOutAPI: IPaymentsOptOut;
    auth: typeof Auth;
}

export default class RegisterAccount extends Component<
    RegisterAccountProps,
    RegisterAccountState
> {
    constructor(props: RegisterAccountProps) {
        super(props);
        this.state = {
            error: "",
            state: State.None,
            isLoading: false,
        };
    }

    handleStripeSubmit = () => {
        this.setState({
            isLoading: true,
        })
        const shopID = localStorage.getItem(Storage.ShopID)
        if (!shopID) {
            localStorage.removeItem(Storage.IsLoggedIn)
            this.props.auth.signOut()
            return this.props.history.push(`${Path.Login}?redirect=${Path.RegisterShopAccount}`);
        }
        this.props.auth
            .currentSession()
            .then((session) => {
                const token = session.getIdToken().getJwtToken();
                this.props.registerAccountAPI
                    .RegisterAccount({
                        identityToken: token,
                        shopID: shopID!,
                        refreshURL:
                            window.location.protocol +
                            "//" +
                            window.location.host +
                            Path.Welcome,
                        returnURL:
                            window.location.protocol +
                            "//" +
                            window.location.host +
                            Path.Welcome,
                    })
                    .then((output) => {
                        if (output.statusCode === 403) {
                            localStorage.removeItem(Storage.IsLoggedIn)
                            return this.props.history.push(`${Path.Login}?redirect=${Path.RegisterShopAccount}`);
                        }

                        if (output.statusCode != 200) {
                            return this.setState({
                                error: "Oop, something unexpected went wrong during the registration. We will investigate why there was an error and get back to you.",
                                isLoading: false,
                            });
                        }

                        return window.location.replace(output.accountLink);
                    })
                    .catch(() =>
                        this.setState({
                            error: "Oop, something unexpected went wrong during the registration. We will investigate why there was an error and get back to you.",
                            isLoading: false,
                        })
                    );
            })
            .catch(() => {
                localStorage.removeItem(Storage.IsLoggedIn)
                return this.props.history.push(`${Path.Login}?redirect=${Path.RegisterShopAccount}`);
            });
    };

    handleOptOutSubmit = () => {
        this.setState({
            isLoading: true,
        })
        const shopID = localStorage.getItem(Storage.ShopID)
        if (!shopID) {
            localStorage.removeItem(Storage.IsLoggedIn)
            return this.props.auth.signOut()
        }
        this.props.auth.currentSession().then(session => {
            this.props.paymentOptOutAPI.PaymentsOptOut({
                identityToken: session.getIdToken().getJwtToken(),
                shopID: shopID,
            }).then(response => {
                if (response.statusCode === 403) {
                    localStorage.removeItem(Storage.IsLoggedIn)
                    return this.props.history.push(`${Path.Login}?redirect=${Path.RegisterShopAccount}`);
                }

                if (response.statusCode !== 200) {
                    return this.setState({
                        error: "Oop, something unexpected went wrong whilst opting-out from payments. We will investigate why there was an error and get back to you.",
                        isLoading: false,
                    });
                }

                return this.props.history.push(Path.Welcome)
            }).catch(() => {
                return this.setState({
                    error: "Oop, something unexpected went wrong whilst opting-out from payments. We will investigate why there was an error and get back to you.",
                    isLoading: false,
                });
            })
        }).catch(() => {
            localStorage.removeItem(Storage.IsLoggedIn)
            return this.props.history.push(`${Path.Login}?redirect=${Path.RegisterShopAccount}`);
        })
    };

    handleStateChange = (state: State) => {
        this.setState({
            error: "",
            state: state,
        })
    }

    render() {
        const selectionProps: SelectionViewProps = {
            error: this.state.error,
            state: this.state.state,
            isLoading: this.state.isLoading,
            onSelect: state => this.setState({ state: state }),
            onOptInSubmit: this.handleStripeSubmit,
            onOptOutSubmit: this.handleOptOutSubmit,
        };
        return <SelectionView {...selectionProps} />;
    }
}

export interface SelectionViewProps {
    error: string
    state: State
    isLoading: boolean
    onSelect: (state: State) => void
    onOptInSubmit: () => void
    onOptOutSubmit: () => void
}

export const SelectionView: FunctionComponent<SelectionViewProps> = (
    props
) => (
    <>
        <Alert variant={"danger"} show={props.error != ""}>
            {props.error}
        </Alert>
        <h1 style={{ marginBottom: "2rem" }}>Select your payment preference</h1>
        <Card className="elevate" style={{ cursor: "pointer", borderLeft: (props.state === State.Online) ? `.8rem solid ${Color.Primary}` : "none" }} onClick={() => props.onSelect(State.Online)}>
            <Card.Body>
                <Card.Title>Online "EasyPay"</Card.Title>
                <Card.Text>
                    Card, Apple Pay, Google Pay — all handled safely online by us (Recommended).
                </Card.Text>
                {(props.state === State.Online) && <Card.Text>
                    <hr />
                    <ul style={{ listStyle: "none", padding: 0, color: Color.DarkGrey }}>
                        <li style={{ marginBottom: "1rem" }}>
                            <strong>Payment Methods:</strong> Card, Apple Pay, Google Pay, and other popular options.
                        </li>
                        <li style={{ marginBottom: "1rem" }}>
                            <strong>Fees:</strong> 3.5% + 20p per transaction is added as a service charge to the customer — you pay nothing.
                        </li>
                        <li style={{ marginBottom: "1rem" }}>
                            <strong>Order Handling:</strong> Payments are authorized immediately but funds are only captured when you mark the order as dispatched or collected.
                        </li>
                        <li style={{ marginBottom: "1rem" }}>
                            <strong>Order Amendment:</strong> No refunds are needed if items are out of stock.
                        </li>
                        <li style={{ marginBottom: "1rem" }}>
                            <strong>Cost to Shop:</strong> Completely free — no monthly or hidden fees.
                        </li>
                        <li style={{ marginBottom: "1rem" }}>
                            <strong>Payment Processor:</strong> Powered by <a href="https://stripe.com/" target={"_"} style={{ color: Color.Primary, fontWeight: 800, textDecoration: "none" }}>Stripe</a>.
                        </li>
                        <li style={{ marginBottom: "1rem" }}>
                            <strong>Setup:</strong> You’ll complete a short form with some business and account info to get started.
                        </li>
                    </ul>
                    <Button buttonvariant={variant.Primary} name={"Select"} disabled={props.isLoading} icon={(props.isLoading) ? icon.Spinner : undefined} style={{ width: "100%" }} onClick={() => props.onOptInSubmit()} />
                </Card.Text>}
            </Card.Body>
        </Card>
        <Card className="elevate" style={{ marginTop: "1rem", cursor: "pointer", borderLeft: (props.state === State.Offline) ? `.8rem solid ${Color.Primary}` : "none" }} onClick={() => props.onSelect(State.Offline)}>
            <Card.Body>
                <Card.Title>Offline "YourWay"</Card.Title>
                <Card.Text>
                    Take payments your way — manage them offline and provide customers with clear instructions through the platform.
                </Card.Text>
                {(props.state === State.Offline) && <Card.Text>
                    <ul style={{ listStyle: "none", padding: 0 }}>
                        <li style={{ marginBottom: "1rem" }}>
                            <strong>Payment Methods:</strong> You choose — cash, card, bank transfer, on delivery, or in-store.
                        </li>
                        <li style={{ marginBottom: "1rem" }}>
                            <strong>Pricing:</strong> Completely free because there are no payment processing fees.
                        </li>
                        <li style={{ marginBottom: "1rem" }}>
                            <strong>Customer Instructions:</strong> Add a custom message to tell customers how to pay (e.g., “Pay in-store” or “Pay on delivery”).
                        </li>
                        <li style={{ marginBottom: "1rem" }}>
                            <strong>Payment Flow:</strong> No payments go through the app — it’s your responsibility to receive payments from customers.
                        </li>
                    </ul>
                    <Button buttonvariant={variant.Primary} name={"Select"} disabled={props.isLoading} style={{ width: "100%" }} icon={(props.isLoading) ? icon.Spinner : undefined} onClick={() => props.onOptOutSubmit()} />
                </Card.Text>}
            </Card.Body>
        </Card>
    </>
)